/* @jsx h */
import {
  defineComponent,
  ref,
  onMounted,
  h,
  watch,
  nextTick,
  onUnmounted,
  onBeforeUnmount,
  VNode,
  Teleport,
} from "vue";
import { useNavStack } from "../FullScreenSheet/navStack";

export type BottomSheetProps = {
  isOpen: boolean;
  title?: string | VNode;
  onClose: Function;
  maxHeight?: string;
  zIndex?: string;
  height?: string;
  class?: string;
  hideClose?: boolean;
  /** Keep the sheet open when the dimmed backdrop is tapped. */
  disableCloseByClickingBackdrop?: boolean;
};
export const BottomSheet = defineComponent<BottomSheetProps>({
  name: "BottomSheet",
  props: [
    "isOpen",
    "title",
    "onClose",
    "maxHeight",
    "zIndex",
    "height",
    "class",
    "hideClose",
    "disableCloseByClickingBackdrop",
  ],
  setup(props, { slots }) {
    const navStack = useNavStack();
    const sheetRef = ref<HTMLDivElement | null>(null);
    const scrollContentRef = ref<HTMLDivElement | null>(null);
    const startY = ref(0);
    const isDragging = ref(false);
    const draggingOffset = ref(0);
    const show = ref(false);
    let sheetId: string | null = null;

    const registerSheet = () => {
      if (sheetId || !navStack) return;
      sheetId = navStack.pushSheet(() => transitionOut());
    };

    const unregisterSheet = () => {
      if (sheetId && navStack) {
        navStack.popSheet(sheetId);
        sheetId = null;
      }
    };

    const transitionIn = () => {
      registerSheet();
      show.value = true;
      nextTick(() => {
        requestAnimationFrame(() => {
          if (sheetRef.value) {
            sheetRef.value.style.transition = "transform 200ms ease-out";
            sheetRef.value.style.transform = `translate3d(0, 0, 0)`;
          }
        });
      });
    };

    const transitionOut = () => {
      unregisterSheet();
      if (sheetRef.value) {
        sheetRef.value.style.transition = "transform 250ms ease-out";
        sheetRef.value.style.transform = `translate3d(0, 100%, 0)`;

        setTimeout(() => {
          show.value = false;
          props.onClose?.();
        }, 250);
      }
    };

    const handleTouchStart = (e: TouchEvent) => {
      if (!scrollContentRef.value) return;
      if (props.hideClose) return;

      const scrollEl = scrollContentRef.value;
      //.querySelector("[data-scroll]");
      if (scrollEl && scrollEl.scrollTop > 0) return; // Don't start drag if user is mid-scroll

      startY.value = e.touches[0].clientY;
      isDragging.value = true;
      draggingOffset.value = 0;

      if (sheetRef.value) {
        sheetRef.value.style.transition = "none";
        sheetRef.value.style.willChange = "transform";
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging.value || !sheetRef.value || !scrollContentRef.value)
        return;
      if (props.hideClose) return;
      //e.preventDefault();
      const scrollEl = scrollContentRef.value;
      //querySelector("[data-scroll]") as HTMLElement;

      // e.preventDefault();

      const deltaY = e.touches[0].clientY - startY.value;
      //      console.log("deltaY", deltaY, scrollEl?.scrollTop);
      if (deltaY < 0) return; // Ignore upward drags
      if (scrollEl?.scrollTop === 0 && deltaY > 0) {
        e.preventDefault();
      }

      draggingOffset.value = deltaY;
      sheetRef.value.style.transform = `translate3d(0, ${deltaY}px, 0)`;
    };

    const handleTouchEnd = () => {
      if (!sheetRef.value || !isDragging.value) return;
      if (props.hideClose) return;

      isDragging.value = false;
      sheetRef.value.style.transition = "transform 250ms ease-out";

      if (draggingOffset.value > 120) {
        transitionOut();
      } else {
        sheetRef.value.style.transform = `translate3d(0, 0, 0)`;
      }

      draggingOffset.value = 0;
    };

    watch(
      () => props.isOpen,
      (newVal) => {
        if (newVal) transitionIn();
        else transitionOut();
      },
    );

    onMounted(() => {
      document.getElementsByTagName("html")[0].style.overflow = "hidden";
      if (props.isOpen) transitionIn();
    });

    onBeforeUnmount(() => {
      unregisterSheet();
    });

    onUnmounted(() => {
      document.getElementsByTagName("html")[0].style.overflow = "auto";
    });

    return () => (
      <Teleport to="body">
        <div
          onTouchstart={handleTouchStart}
          onTouchmove={handleTouchMove}
          onTouchend={handleTouchEnd}
          class={
            "fixed inset-0 bg-black/50 " + (props.zIndex ? props.zIndex : "")
          }
          onClick={() => {
            if (!props.disableCloseByClickingBackdrop) props.onClose();
          }}
        >
          <div
            ref={sheetRef}
            onClick={(e) => e.stopPropagation()}
            class={`border  fixed shadow-1xl overflow-hidden ${props.class || "bg-base-100"}`}
            style={`
              transform: translate3d(0, 100%, 0);
              will-change: transform;
              transition: transform 250ms ease-out;
              display: flex;
              flex-direction: column;
              height: ${props.height || "auto"};
              max-height: calc(100dvh - 60px - var(--safe-area-top, 0px));
              border-radius: 7px 7px 0px 0px;
              bottom: 0;
              left: -1px;
              right: -1px;
              width: calc(100% + 2px)
            `}
          >
            {/* Drag handle */}
            <div
              style={`transform: translateX(-50%);`}
              class="absolute top-[-9px] left-[50%] flex justify-center pt-3 pb-1 shrink-0"
            >
              <div class="w-10 h-1 rounded-full bg-base-content/20" />
            </div>
            {/* Header */}
            {(props.title || !props.hideClose) && (
              <div class="flex items-center px-4 pt-2 pb-2 shrink-0 border-b">
                {/* Left spacer mirrors close button to keep title centered */}
                <div class="w-8 h-8 shrink-0" />
                <div class="flex-1 text-2xl text-accent flex justify-center ">
                  {props.title ? <div class="  ">{props.title}</div> : null}
                </div>
                {props.hideClose ? (
                  <div class="w-8 h-8 shrink-0" />
                ) : (
                  <button
                    class="btn btn-ghost btn-sm btn-circle text-base-content/60 shrink-0"
                    onClick={() => {
                      props.onClose && props.onClose();
                    }}
                  >
                    <span class="material-symbols-outlined text-xl">close</span>
                  </button>
                )}
              </div>
            )}
            <div
              ref={scrollContentRef}
              class="overflow-y-auto flex flex-col flex-1 bg-base-100"
              style="flex: 1; -webkit-overflow-scrolling: touch; overscroll-behavior: contain; will-change: scroll-position;"
            >
              {slots.default?.()}
            </div>
          </div>
        </div>
      </Teleport>
    );
  },
});
